import React from "react";
import { useLocation } from "react-router-dom";
import { ArrowUpRight, Download } from "lucide-react";
import PortfolioText from "./PortfolioText";
import MediaLightbox from "./MediaLightbox";
import Header from "./Header";
import Footer from "./Footer";
import WorkTile from "./WorkTile";
import ShowcaseVisual from "./ShowcaseVisual";
import { VisualBackdrop } from "./VisualLayer";
import { ExpandOverlay, MediaVideo } from "./WorkMedia";
import { stageBackdrop, tileFor } from "../../data/workIndex";
import useBackLink from "../../hooks/useBackLink";

const ALL_WORK = { to: "/work", label: "All work" };

// THE ARTICLE SHELL — the four case studies and the motion demo run on this.
// Everything below the title is optional; the demo page is a title, a
// standfirst and one component, and gets the same frame as a case study.
//
// A case study here is a short pitch read by someone deciding whether to book
// a call, so the page is built to be scanned first and read second. The
// masthead answers what, how much and who before the hero: title, standfirst,
// the outcomes as display figures, then the facts. Chapters are numbered and
// labelled instead of listed in a contents rail, and the parts below (Timeline,
// Points) exist so the one story told in depth reads as steps rather than as
// paragraphs.
//
// Type, widths and spacing live in src/styles/research-article.css, expressed
// as the relationship between two neighbours rather than as a gap on any one
// element, so nothing here hardcodes a distance.

export const slugify = (value) =>
  String(value)
    .toLowerCase()
    .replace(/[^\w]+/g, "-")
    .replace(/^-|-$/g, "");

// -----------------------------------------------------------------
// Typographic parts
// -----------------------------------------------------------------

// Chapter. The stylesheet numbers it; `tocLabel` names what kind of chapter it
// is ("The problem", "Adoption") above the heading, which carries the claim.
// The id is derived from the heading unless the page gives one, so a chapter
// can still be linked to.
export function Section({ id, heading, tocLabel, children }) {
  return (
    <section id={id || slugify(heading)} className="pf-ra-section">
      <PortfolioText
        as="h2"
        variant="headline-sm-bold"
        treatment="none"
        colorTokenName="--pf-ra-ink"
        className="pf-ra-h2"
      >
        <span className="pf-ra-kicker">{tocLabel}</span>
        {heading}
      </PortfolioText>
      {children}
    </section>
  );
}

// Sub-heading inside a chapter, for the longer pages that still use Beat.
export function Sub({ children }) {
  return (
    <PortfolioText
      as="h3"
      variant="headline-xs-semibold"
      treatment="none"
      colorTokenName="--pf-ra-ink"
      className="pf-ra-h3"
    >
      {children}
    </PortfolioText>
  );
}

// Body copy is a step softer than the headings, so the headings hold the page
// without needing more size.
export function Body({ children }) {
  return (
    <PortfolioText
      variant="article-md-regular"
      treatment="none"
      colorTokenName="--pf-ra-ink-body"
      className="pf-ra-p"
    >
      {children}
    </PortfolioText>
  );
}

// Standfirst, in the masthead under the title.
export function Lead({ children }) {
  return (
    <PortfolioText
      variant="article-lg-regular"
      treatment="none"
      colorTokenName="--typography-text-secondary"
      className="pf-ra-lead"
    >
      {children}
    </PortfolioText>
  );
}

// The single most important line in a chapter: larger than anything but the
// headings, on an accent rule, with a figure's air around it. The wrapper
// takes the flow's spacing so the rule only spans the text.
export function Standout({ children }) {
  return (
    <div className="pf-ra-standout">
      <PortfolioText
        variant="article-lg-regular"
        treatment="none"
        colorTokenName="--pf-ra-ink"
        className="pf-ra-standout__text"
      >
        {children}
      </PortfolioText>
    </div>
  );
}

// A titled move: sub-heading, the paragraph that explains it, and optionally
// the artwork that shows it.
//
// It returns a FRAGMENT, not a wrapper. The three parts have to be direct
// children of the section for the flow rules to space them — a div around them
// would isolate them from the rhythm and reintroduce the per-block gaps this
// stylesheet exists to remove.
export function Beat({ title, copy, figure }) {
  return (
    <>
      <Sub>{title}</Sub>
      {copy && <Body>{copy}</Body>}
      {figure && <Figure {...figure} />}
    </>
  );
}

// A set of named parts — the "key points" shape. A grid of short terms, not a
// run of sub-headings: as headings they competed with the chapter titles.
export function Points({ items }) {
  return (
    <dl className="pf-ra-points">
      {items.map((item) => (
        <div key={item.title} className="pf-ra-points__item">
          <dt>{item.title}</dt>
          <dd>{item.copy}</dd>
        </div>
      ))}
    </dl>
  );
}

// A story told as steps on a rule: what happened, in order, each step one
// bold line and one sentence. `when` is optional and only set where the date
// is known.
export function Timeline({ items }) {
  return (
    <ol className="pf-ra-timeline">
      {items.map((item) => (
        <li key={item.title} className="pf-ra-timeline__step">
          <p className="pf-ra-timeline__title">
            {item.when && <span className="pf-ra-timeline__when">{item.when}</span>}
            {item.title}
          </p>
          {item.copy && <p className="pf-ra-timeline__copy">{item.copy}</p>}
        </li>
      ))}
    </ol>
  );
}

// Plain bulleted run, at body size and inside the reading measure.
export function List({ items, ordered = false }) {
  const Tag = ordered ? "ol" : "ul";
  return (
    <Tag className={ordered ? "pf-ra-list pf-ra-list--ordered" : "pf-ra-list"}>
      {items.map((line) => (
        <li key={line}>
          <PortfolioText
            variant="article-md-regular"
            treatment="none"
            colorTokenName="--pf-ra-ink-body"
            className="pf-ra-p"
          >
            {line}
          </PortfolioText>
        </li>
      ))}
    </Tag>
  );
}

// Figure caption, left-aligned on the figure's own left edge (not centred
// under the artwork): the label in the text colour, the description in
// secondary on the line below.
export function Caption({ label, children }) {
  return (
    <figcaption className="pf-ra-caption">
      {label && (
        <PortfolioText
          as="span"
          variant="article-sm-medium"
          treatment="none"
          colorTokenName="--pf-ra-ink"
          className="pf-ra-caption__label"
        >
          {label}
        </PortfolioText>
      )}
      {children && (
        <PortfolioText
          as="span"
          variant="article-sm-regular"
          treatment="none"
          colorTokenName="--typography-text-secondary"
          className="pf-ra-caption__copy"
        >
          {children}
        </PortfolioText>
      )}
    </figcaption>
  );
}

// Article figure. By default it takes the media measure — the hero's width —
// from the text's left edge, and falls back to the column width once the
// viewport can no longer hold it.
//
// Without a `src` it prints the themed gradient placeholder the cards use, so
// pages whose artwork is still being recreated keep their rhythm. With one it
// is expandable through the same MediaLightbox the gallery cards use.
//
// Given a `backdrop` (the hero, from the piece's homepage stage), the media is
// a card centred on that backdrop instead of filling the frame.
//
// `frame` ("browser" or "browser-dark") puts the media in the same minimal
// browser window the homepage stages use, for full-viewport captures of a
// site. Framed media is 3:2.
//
// `narrowSrc` swaps in a reflowed image below the 640px breakpoint, for
// diagrams whose labels would shrink past reading at phone width. The
// lightbox keeps the full image.
//
// `visual` replaces the media with a stage of real screens laid out like the
// homepage's (see data/articleStages.js), on the stage colour or, as the
// hero, on the piece's backdrop. It expands into the dialog like an image.
export function Figure({
  src,
  narrowSrc,
  alt,
  label,
  caption,
  type = "image",
  width = "wide",
  height = 360,
  expandable = true,
  backdrop,
  frame,
  visual,
}) {
  const [previewOpen, setPreviewOpen] = React.useState(false);
  if (visual) {
    const staged = { ...visual, alt: visual.alt || alt, backdrop: visual.backdrop || backdrop };
    return (
      <figure className={`pf-ra-figure pf-ra-figure--${width}`}>
        <div className={`relative${expandable ? " pf-expandable" : ""}`}>
          <ShowcaseVisual
            project={{ id: "", href: "", title: "" }}
            visual={staged}
            priority={width === "hero"}
            linked={false}
          />
          {expandable && (
            <ExpandOverlay
              onExpand={() => setPreviewOpen(true)}
              label={`${label || caption || staged.alt || "Image"} — open preview`}
              radius="var(--radius-200)"
            />
          )}
        </div>
        {(label || caption) && <Caption label={label}>{caption}</Caption>}
        {expandable && (
          <MediaLightbox
            open={previewOpen}
            items={[{ type: "visual", visual: staged, alt: staged.alt }]}
            title={label || caption || staged.alt || ""}
            onClose={() => setPreviewOpen(false)}
          />
        )}
      </figure>
    );
  }
  const canExpand = Boolean(src) && expandable;
  const staged = Boolean(src && backdrop);
  const framed = Boolean(src && frame);
  const radius = staged ? "var(--radius-150)" : "var(--pf-ra-media-radius)";
  const lift = canExpand ? " pf-expandable__media" : "";
  const mediaClass =
    (staged && !framed ? "pf-ra-stage__media" : "block h-auto w-full") + (framed ? "" : lift);
  const media =
    type === "video" ? (
      <MediaVideo src={src} className={mediaClass} />
    ) : narrowSrc ? (
      <picture>
        <source media="(max-width: 639px)" srcSet={narrowSrc} />
        <img src={src} alt={alt} className={mediaClass} />
      </picture>
    ) : (
      <img src={src} alt={alt} className={mediaClass} />
    );

  return (
    <figure className={`pf-ra-figure pf-ra-figure--${width}`}>
      {src ? (
        <div
          className={`relative w-full overflow-hidden${staged ? " pf-ra-stage" : ""}${
            canExpand ? " pf-expandable" : ""
          }`}
          style={{ borderRadius: radius }}
        >
          {staged && <VisualBackdrop backdrop={backdrop} priority />}
          {framed ? (
            <div
              className={`pf-ra-browser${frame === "browser-dark" ? " pf-ra-browser--dark" : ""}${
                staged ? " pf-ra-browser--staged" : ""
              }${lift}`}
            >
              <span className="pf-ra-browser__bar" aria-hidden="true">
                <i />
                <i />
                <i />
                <b />
              </span>
              {media}
            </div>
          ) : (
            media
          )}
          {canExpand && (
            <ExpandOverlay
              onExpand={() => setPreviewOpen(true)}
              label={`${label || caption || alt || "Image"} — open preview`}
              radius={radius}
            />
          )}
        </div>
      ) : (
        <div
          role="img"
          aria-label={alt}
          className="w-full"
          style={{
            height,
            borderRadius: radius,
            background: "linear-gradient(180deg, var(--container-featured-gradient))",
          }}
        />
      )}
      {(label || caption) && <Caption label={label}>{caption}</Caption>}
      {canExpand && (
        <MediaLightbox
          open={previewOpen}
          items={[{ type, src, alt }]}
          title={label || caption || alt || ""}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </figure>
  );
}

// Numbers, in the reference's table register: hairline rules, no surface, no
// shadow. The reference never puts a card inside the reading flow — its
// quantitative blocks are all ruled tables.
export function Stats({ caption, label, metrics }) {
  return (
    <figure className="pf-ra-figure pf-ra-figure--column">
      <dl className="pf-ra-stats">
        {metrics.map((m) => (
          <div key={m.label} className="pf-ra-stats__cell">
            <PortfolioText as="dd" variant="headline-sm-bold" treatment="secondary-md">
              {m.value}
            </PortfolioText>
            <PortfolioText
              as="dt"
              variant="body-sm-regular"
              treatment="none"
              colorTokenName="--typography-text-secondary"
            >
              {m.label}
            </PortfolioText>
          </div>
        ))}
      </dl>
      {(label || caption) && <Caption label={label}>{caption}</Caption>}
    </figure>
  );
}

// The reference closes on footnotes: small, numbered, secondary, under a rule.
// The case studies have the same thing under a different name — the boundaries
// of what each number actually claims — so they render here.
export function Notes({ heading = "Notes", items }) {
  return (
    <section className="pf-ra-notes">
      <PortfolioText
        as="h2"
        variant="body-sm-medium"
        treatment="none"
        colorTokenName="--pf-ra-ink"
        className="pf-ra-notes__title"
      >
        {heading}
      </PortfolioText>
      <ol className="pf-ra-notes__list">
        {items.map((line) => (
          <li key={line}>
            <PortfolioText
              variant="article-sm-regular"
              treatment="none"
              colorTokenName="--typography-text-secondary"
            >
              {line}
            </PortfolioText>
          </li>
        ))}
      </ol>
    </section>
  );
}

// -----------------------------------------------------------------
// Shell parts
// -----------------------------------------------------------------

// What the work achieved, as display figures in the masthead: the first
// numbers a scanner meets, before any prose.
function Outcomes({ items }) {
  return (
    <dl className="pf-ra-outcomes">
      {items.map((m) => (
        <div key={m.label} className="pf-ra-outcomes__cell">
          <dt className="pf-ra-outcomes__label">{m.label}</dt>
          <dd className="pf-ra-outcomes__value">{m.value}</dd>
        </div>
      ))}
    </dl>
  );
}

// Facts of the project — role, team, when, company — in one row under the
// outcomes: label above value, small, so they support the figures rather than
// compete with them.
function Facts({ items }) {
  return (
    <dl className="pf-ra-facts">
      {items.map((m) => (
        <div key={m.label} className="pf-ra-facts__cell">
          <dt className="pf-ra-facts__label">{m.label}</dt>
          <dd className="pf-ra-facts__value">{m.value}</dd>
        </div>
      ))}
    </dl>
  );
}

// Reading-progress line, carried over from the previous article shell: the
// fill grows from 0 to the page width as the reader scrolls, in the themed
// --indicator-default so the accent tracks the active theme.
//
// It is its own sticky strip rather than a layer inside the Header, so the
// landing page's header is not touched to get it. Zero height and a negative
// margin keep it out of the flow; a higher z-index than the Header's 50 puts
// the line over the bar's top edge, where the old one sat.
function ReadingProgress() {
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    const update = () => {
      const el = document.documentElement;
      const max = el.scrollHeight - el.clientHeight;
      setProgress(max > 0 ? Math.min(100, (el.scrollTop / max) * 100) : 0);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  return (
    <div className="pf-ra-progress" aria-hidden="true">
      <div className="pf-ra-progress__fill" style={{ width: `${progress}%` }} />
    </div>
  );
}

// Opens whatever a deck / CTA points at. A mailto: has no document to load, so
// window.open would leave an empty tab behind after the mail client takes over.
// External destinations open in a new tab. A deck is a same-origin PDF and
// should save to disk rather than open in the browser's viewer, so it goes
// through an anchor with `download`, which browsers honour same-origin.
function openTarget(target) {
  if (!target) return;
  if (target.href.startsWith("mailto:")) {
    window.location.assign(target.href);
    return;
  }
  if (target.external) {
    window.open(target.href, "_blank", "noopener,noreferrer");
    return;
  }
  const anchor = document.createElement("a");
  anchor.href = target.href;
  anchor.download = target.href.split("/").pop() || "";
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

// The homepage's primary pill, as a button: it downloads or opens a new tab
// through openTarget rather than navigating.
function DeckButton({ deck }) {
  const Icon = deck.external ? ArrowUpRight : Download;
  return (
    <button type="button" className="pf-pill pf-pill--primary" onClick={() => openTarget(deck)}>
      <span>
        {deck.label || "Download full deck"}
        {deck.external && <span className="pf-visually-hidden"> (opens in a new tab)</span>}
      </span>
      <Icon size={16} strokeWidth={1.75} aria-hidden="true" />
    </button>
  );
}

// The deck / CTA, at the end of the reading column. The masthead pill is the
// same destination in one word; this is where it gets described.
//
// The reference closes on Citation and Acknowledgements — ruled blocks under a
// small secondary heading, no surface. Same register here, so the offer reads
// as the article's colophon rather than as a card dropped in at the end.
function ClosingBlock({ deck }) {
  return (
    <section className="pf-ra-closing">
      <PortfolioText
        as="h2"
        variant="body-md-medium"
        treatment="none"
        className="pf-ra-closing__title"
      >
        {deck.title || "Project deck"}
      </PortfolioText>
      <div className="pf-ra-closing__body">
        <PortfolioText
          variant="article-sm-regular"
          treatment="none"
          colorTokenName="--typography-text-secondary"
        >
          {deck.description}
        </PortfolioText>
        <div className="pf-ra-closing__action">
          <DeckButton deck={deck} />
        </div>
      </div>
    </section>
  );
}

// Bottom-of-page "Related content": the next pieces as the /work tiles, at the
// media measure. Items come from src/data/articles.js, so `path` is the
// internal route the tile is looked up by.
function Related({ items }) {
  const tiles = items.map((item) => tileFor(item.path)).filter(Boolean);
  if (tiles.length === 0) return null;

  return (
    <section className="pf-ra-frame pf-ra-frame--media pf-ra-related" aria-labelledby="related-heading">
      <div className="pf-section__head">
        <h2 id="related-heading" className="pf-section__title">
          Related content
        </h2>
      </div>
      <ul className="pf-tiles pf-tiles--3">
        {tiles.map((tile) => (
          <li key={tile.id}>
            <WorkTile tile={tile} />
          </li>
        ))}
      </ul>
    </section>
  );
}

// -----------------------------------------------------------------

// Every part below the title is optional, so the same frame carries a full case
// study (outcomes, meta, hero, chapters, related) and a bare page like the
// motion demo, which is a title, a standfirst and the demo itself.
function ResearchArticle({ eyebrow, title, lead, outcomes, meta, deck, hero, related, children }) {
  const { pathname } = useLocation();
  const back = useBackLink(ALL_WORK);
  const heroBackdrop = hero && (hero.backdrop || stageBackdrop(pathname));

  return (
    <div className="pf-ra min-h-screen w-full">
      {/* Before the Header, not after: as the first child it is already at
          y=0, so the line is on the bar's top edge from the first pixel of
          scroll instead of riding up to meet it. */}
      <ReadingProgress />
      {/* The way back out of the article is the bar's own. */}
      <Header back={back} />

      <main id="main" tabIndex={-1} className="pf-ra-main">
      <article>
        {/* Masthead: what, how much, who — before the hero, on the same
            left edge as everything below it. */}
        <header className="pf-ra-frame pf-ra-masthead">
          <div className="pf-ra-masthead__inner">
            {eyebrow && (
              <PortfolioText
                as="p"
                variant="body-sm-medium"
                treatment="none"
                colorTokenName="--typography-text-secondary"
              >
                {eyebrow}
              </PortfolioText>
            )}
            <PortfolioText
              as="h1"
              variant="headline-xl-bold"
              treatment="none"
              colorTokenName="--pf-ra-ink"
              className="pf-ra-title"
            >
              {title}
            </PortfolioText>
            {lead && <Lead>{lead}</Lead>}
            {deck && (
              <div className="pf-ra-masthead__action">
                <DeckButton deck={deck} />
              </div>
            )}
            {outcomes && outcomes.length > 0 && <Outcomes items={outcomes} />}
            {meta && meta.length > 0 && <Facts items={meta} />}
          </div>
        </header>

        {hero && (
          <div className="pf-ra-frame pf-ra-hero">
            <Figure {...hero} backdrop={heroBackdrop} width="hero" />
          </div>
        )}

        <div className="pf-ra-frame pf-ra-layout">
          <div className="pf-ra-layout__column">
            <div className="pf-ra-flow">{children}</div>
            {deck && <ClosingBlock deck={deck} />}
          </div>
        </div>

        {related && related.length > 0 && <Related items={related} />}
      </article>
      </main>

      <Footer />
    </div>
  );
}

export default ResearchArticle;
